import axios from 'axios';
import ProjectConfig from 'ProjectConfig';

const API_GATEWAY = ProjectConfig.API_GATEWAY;

const USER_PREDICTIONS_PATH = eventId =>
  `${API_GATEWAY}/sports-data/user/matches/v1/bahista/predictions/${eventId}`;

const MATCH_PREDICTIONS_PATH = eventId =>
  `${API_GATEWAY}/sports-data/matches/v1/bahista/predictions/${eventId}`;

const BAHISTA_AUTHOR_PATH = `${API_GATEWAY}/v2/authors/public/bahista`;

export const FALLBACK_BAHISTA_AUTHOR_ID = 477;

export const getBahistaAuthorUrl = authorId =>
  `/yazarlar/yazar-detay/bahista-${authorId || FALLBACK_BAHISTA_AUTHOR_ID}`;

export const mapBahistaAuthor = data => {
  if (!data || typeof data !== 'object') return null;
  const id = data.id ?? data.authorId;
  if (id == null || id === '') return null;
  return {
    id,
    name: data.name || '',
    avatar: data.avatar || '',
    profile: data.profile || '',
    leagues: data.leagues || '',
    profession: data.profession || '',
    sportId: data.sportId,
    sportType: data.sportType,
  };
};

const CONFIDENCE_TO_PROBABILITY = {
  HIGH: 'HIGH',
  MEDIUM: 'BALANCED',
  LOW: 'SURPRISE',
  BALANCED: 'BALANCED',
  SURPRISE: 'SURPRISE',
};

const buildCustomerHeaders = customerId => {
  if (customerId == null || customerId === '') return undefined;
  return { customerId: String(customerId) };
};

const isAnalysisRequested = value => value === true || value === 'true';

const serviceError = (message, code = 'SERVICE') => {
  const err = new Error(message);
  err.code = code;
  return err;
};

const firstObject = value => {
  if (Array.isArray(value)) {
    return value.find(item => item && typeof item === 'object') || {};
  }
  return value && typeof value === 'object' ? value : {};
};

const resolveOddPayload = item => firstObject(
  item?.odd
  || item?.odds
  || item?.market
  || item?.marketInfo
  || item?.marketResponse
  || item?.oddResponse
);

const hasOddPayload = odd => Object.keys(odd).length > 0;

export const mapBahistaPrediction = item => {
  if (!item) return null;

  const odd = resolveOddPayload(item);
  const confidence = String(item.confidenceLevel || '').toUpperCase();
  const probability = CONFIDENCE_TO_PROBABILITY[confidence] || null;

  return {
    title: item.message || '',
    probability,
    analysis: Array.isArray(item.analysisResult) ? item.analysisResult : [],
    showCurrentOdds: !!item.showCurrentOdds,
    market: hasOddPayload(odd) ? {
      marketId: odd.marketId,
      marketOutcomeId: odd.marketOutcomeId ?? odd.marketOutcomeNo ?? odd.outcomeNo,
      marketStatus: odd.marketStatus ?? odd.status,
      currentOddsWeb: odd.currentOddsWeb ?? odd.currentOdds,
      fixedOddsWeb: odd.fixedOddsWeb ?? odd.fixedOdds,
      label: odd.oddName || odd.marketOutcomeName || odd.label || odd.name || '',
      marketName: odd.marketName || '',
      mbs: odd.mbs,
      hasTopWin: !!odd.hasTopWin,
      oddsWithTopWin: odd.oddsWithTopWin,
    } : null,
  };
};

export const mapBahistaPredictionsResponse = data => {
  const raw = Array.isArray(data?.predictions) ? data.predictions : [];
  return {
    eventId: data?.eventId,
    authorId: data?.authorId,
    homeTeam: data?.homeTeam,
    awayTeam: data?.awayTeam,
    eventName: data?.eventName,
    leagueName: data?.leagueName,
    sportId: data?.sportId,
    predictions: raw.map(mapBahistaPrediction).filter(Boolean),
  };
};

export const checkUserBahistaAnalysis = async (eventId, { customerId } = {}) => {
  if (!eventId) throw serviceError('BAHISTA_SERVICE_UNAVAILABLE');

  try {
    const res = await axios.get(USER_PREDICTIONS_PATH(eventId), {
      headers: buildCustomerHeaders(customerId),
    });
    const data = typeof res?.data === 'string' ? JSON.parse(res.data) : res?.data;
    return {
      eventId: data?.eventId,
      hasAnalysisRequested: isAnalysisRequested(data?.hasAnalysisRequested),
      requestedWhileLive: isAnalysisRequested(data?.requestedWhileLive),
    };
  } catch (e) {
    const err = serviceError('BAHISTA_SERVICE_UNAVAILABLE');
    err.cause = e;
    throw err;
  }
};

export const requestBahistaAnalysis = async (eventId, { customerId } = {}) => {
  if (!eventId) throw serviceError('BAHISTA_ANALYZE_FAILED', 'ANALYZE');

  try {
    const res = await axios.post(USER_PREDICTIONS_PATH(eventId), {}, {
      headers: buildCustomerHeaders(customerId),
    });
    return typeof res?.data === 'string' ? JSON.parse(res.data) : (res?.data || {});
  } catch (e) {
    const err = serviceError('BAHISTA_ANALYZE_FAILED', 'ANALYZE');
    err.cause = e;
    throw err;
  }
};

export const fetchBahistaPredictions = async (eventId, { preview = false } = {}) => {
  if (!eventId) throw serviceError('BAHISTA_ANALYZE_FAILED', 'ANALYZE');

  try {
    const res = await axios.get(MATCH_PREDICTIONS_PATH(eventId), {
      params: { preview: !!preview },
    });
    const data = typeof res?.data === 'string' ? JSON.parse(res.data) : res?.data;
    return mapBahistaPredictionsResponse(data);
  } catch (e) {
    const err = serviceError(
      preview ? 'BAHISTA_SERVICE_UNAVAILABLE' : 'BAHISTA_ANALYZE_FAILED',
      preview ? 'SERVICE' : 'ANALYZE'
    );
    err.cause = e;
    throw err;
  }
};

export const runBahistaAnalyze = async (eventId, { isAuthenticated, customerId } = {}) => {
  if (isAuthenticated && customerId != null && customerId !== '') {
    await requestBahistaAnalysis(eventId, { customerId });
  }
  return fetchBahistaPredictions(eventId, { preview: !isAuthenticated });
};

export const fetchBahistaAuthor = async sportId => {
  if (sportId == null || sportId === '') return null;

  try {
    const res = await axios.get(BAHISTA_AUTHOR_PATH, {
      params: { sportId },
    });
    const data = typeof res?.data === 'string' ? JSON.parse(res.data) : res?.data;
    return mapBahistaAuthor(data);
  } catch (e) {
    return null;
  }
};
